/**
 * Die Eingabe des sechsstelligen Sicherheitscodes (HM-060).
 *
 * Aufbau aus `templates/auth-web/AuthWeb.dc.html`: sechs Kästchen nebeneinander,
 * das aktive grün umrandet, die noch leeren gedämpft. Die Stellenzahl kommt aus
 * der **Antwort des Servers** und nicht aus einer Zahl in dieser Datei — wer sie
 * im Vertrag ändert, ändert sie damit auch hier.
 *
 * **Hier und nicht in `@hausmeter/ui`**, wie `password-field.tsx`: Die
 * Komponente lebt von `keydown`, `paste` und der Fokusverwaltung über mehrere
 * Felder — DOM-Ereignisse, die es in React Native nicht gibt. Der Hauptindex
 * des Design-Systems muss dafür taugen (siehe `chart`, ADR 0021).
 *
 * Vier Dinge tragen sie:
 *
 * **Sechs Felder sind eine einzige Eingabe.** Nach außen gibt es eine
 * Zeichenkette; die Kästchen sind Darstellung. Sechs getrennte Zustände wären
 * sechs Gelegenheiten, sie auseinanderlaufen zu lassen.
 *
 * **Eingefügt wird der ganze Code.** Wer ihn aus dem Postfach kopiert, hat sechs
 * Ziffern in der Zwischenablage — womöglich mit Leerzeichen. Ohne eigene
 * Behandlung landete davon genau eine im ersten Kästchen, und der Rest wäre
 * verloren.
 *
 * **Der Rücklauf ist wichtiger als der Vorlauf.** `Backspace` in einem leeren
 * Feld springt zurück und löscht dort — sonst muss man erst klicken, um einen
 * Zahlendreher zu berichtigen, und das an einem Formular mit fünf Versuchen.
 *
 * **Und abgeschickt wird nicht von selbst.** Die sechste Ziffer löst das
 * Absenden **nicht** aus, obwohl das anderswo üblich ist: Jeder Versuch zählt,
 * und nach fünf Fehlversuchen ist der Vorgang beendet. Wer sich vertippt, soll
 * die Zeile lesen können, bevor sie einen davon kostet.
 *
 * **Die Kästchen liegen in einem Raster und nicht in einer Flex-Zeile**, und
 * das ist keine Geschmacksfrage — daran hing die ganze Anmeldeansicht. Ein
 * `<input>` ohne `size` ist rund zwanzig Zeichen breit; bei 24 px Monoschrift
 * sind das etwa 290 px, sechsmal also 1780 px. In einer Flex-Zeile bestimmt
 * diese **Maximalbreite** die Mindestbreite des Kastens: Ein Element mit
 * `flex: 1` und `flex-basis: 0` trägt bei der Größenermittlung seine
 * Wunschbreite bei, und `min-width: 0` an den Feldern ändert daran nichts. Die
 * Formularspalte der Anmeldung war dadurch **1966 px** breit statt 400, die
 * Markenfläche daneben rutschte darüber, und der Rest stand über den Rand der
 * Karte hinaus. Ein Raster aus `minmax(0, 1fr)` misst die Spalten am
 * verfügbaren Platz und nicht am Inhalt; `size={1}` nimmt derselben Ursache
 * zusätzlich den Boden.
 */
import { useRef, useState, type ClipboardEvent, type KeyboardEvent } from "react";

export interface CodeFieldProps {
  readonly label: string;
  /** Stellenzahl — kommt aus `RegistrationStarted.codeLength`. */
  readonly length: number;
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly disabled?: boolean;
  readonly autoFocus?: boolean;
}

const ONLY_DIGITS = /\D/g;

export function CodeField({
  label,
  length,
  value,
  onChange,
  disabled = false,
  autoFocus = false,
}: CodeFieldProps) {
  const [focused, setFocused] = useState<number | null>(null);
  const fields = useRef<(HTMLInputElement | null)[]>([]);

  const digits = value.slice(0, length).split("");

  function focus(index: number): void {
    const target = fields.current[Math.max(0, Math.min(length - 1, index))];
    target?.focus();
    target?.select();
  }

  /**
   * Setzt eine Stelle und rückt weiter.
   *
   * **Ein Klick in ein Feld hinter dem Ende hängt die Ziffer ans Ende**, statt
   * eine Lücke zu lassen. Ein Code mit einem Loch in der Mitte wäre keine
   * Eingabe, sondern ein Zustand, den man erst wieder auflösen muss.
   */
  function setDigit(index: number, digit: string): void {
    const next = value.split("");

    if (digit === "") {
      next.splice(index, 1);
      onChange(next.join(""));
      return;
    }

    const at = Math.min(index, next.length);
    next[at] = digit;
    onChange(next.join("").slice(0, length));
    focus(at + 1);
  }

  function handleKeyDown(index: number, event: KeyboardEvent<HTMLInputElement>): void {
    if (event.key === "Backspace") {
      event.preventDefault();
      // Im leeren Feld zurückspringen und dort löschen; sonst dieses leeren.
      const target = digits[index] === undefined || digits[index] === "" ? index - 1 : index;
      if (target < 0) return;
      const next = value.split("");
      next.splice(target, 1);
      onChange(next.join(""));
      focus(target);
      return;
    }

    if (event.key === "ArrowLeft") {
      event.preventDefault();
      focus(index - 1);
      return;
    }

    if (event.key === "ArrowRight") {
      event.preventDefault();
      focus(index + 1);
    }
  }

  function handlePaste(event: ClipboardEvent<HTMLInputElement>): void {
    const pasted = event.clipboardData.getData("text").replace(ONLY_DIGITS, "");
    if (pasted.length === 0) return;

    event.preventDefault();
    onChange(pasted.slice(0, length));
    focus(Math.min(pasted.length, length - 1));
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {/*
        Ein `role="group"` mit Namen statt sechs Feldern ohne Zusammenhang: Ein
        Screenreader liest sonst sechsmal „Eingabefeld" und nie, wozu.
      */}
      <div
        role="group"
        aria-label={label}
        style={{
          display: "grid",
          gridTemplateColumns: `repeat(${length}, minmax(0, 1fr))`,
          gap: 8,
        }}
        onPaste={handlePaste}
      >
        {Array.from({ length }, (_unused, index) => (
          <input
            key={index}
            ref={(element) => {
              fields.current[index] = element;
            }}
            value={digits[index] ?? ""}
            inputMode="numeric"
            /*
              Nur am ersten Feld: Der Browser bietet den Code aus der SMS oder
              der Mail genau einmal an, und er landet dann über `onPaste` in
              allen sechs Kästchen.
            */
            autoComplete={index === 0 ? "one-time-code" : "off"}
            autoFocus={autoFocus && index === 0}
            disabled={disabled}
            aria-label={`Ziffer ${index + 1} von ${length}`}
            maxLength={1}
            /* Siehe oben: die Wunschbreite eines Feldes ohne `size` ist der
               Grund, aus dem die Spalte einmal 1966 px breit war. */
            size={1}
            onFocus={() => setFocused(index)}
            onBlur={() => setFocused(null)}
            onKeyDown={(event) => handleKeyDown(index, event)}
            onChange={(event) => {
              const digit = event.target.value.replace(ONLY_DIGITS, "").slice(-1);
              setDigit(index, digit);
            }}
            style={{
              minWidth: 0,
              height: 56,
              padding: 0,
              textAlign: "center",
              background:
                digits[index] === undefined ? "var(--surface-sunken)" : "var(--surface-card)",
              border: `1px solid ${
                focused === index ? "var(--hm-green-500)" : "var(--border-default)"
              }`,
              borderRadius: "var(--radius-md)",
              boxShadow:
                focused === index
                  ? "0 0 0 var(--ring-width) var(--focus-ring)"
                  : "var(--shadow-xs)",
              outline: "none",
              fontFamily: "var(--font-mono)",
              fontSize: 24,
              fontWeight: 700,
              color: "var(--text-strong)",
              transition: "border-color .15s ease, box-shadow .15s ease",
              opacity: disabled ? 0.6 : 1,
            }}
          />
        ))}
      </div>
    </div>
  );
}
